'use client';

import Image from 'next/image';
import Link from 'next/link';
import Header from '@/components/common/Header';
import Icon from '@/components/ui/AppIcon';
import { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import GeneralEntrepreneurOnboarding from '@/components/onboarding/GeneralEntrepreneurOnboarding';
import { isOnboardingCompleted } from '@/services/onboardingService';

export default function StartPage() {
  const { isAuthenticated, user, loading } = useAuth();
  const [ideaMode, setIdeaMode] = useState(false);
  const [showOnboarding, setShowOnboarding] = useState(false);

  useEffect(() => {
    if (!loading && user) {
      isOnboardingCompleted(user.id).then((completed) => {
        if (!completed) setShowOnboarding(true);
      });
    }
  }, [user, loading]);

  const buildCoachHref = (path: string) =>
    isAuthenticated ? path : `/login?redirect=${encodeURIComponent(path)}`;

  const renderBenefitList = (items: Array<{ icon: string; text: string }>) => (
    <ul className="mx-auto mt-6 max-w-md space-y-2.5 text-left text-sm text-sky-100">
      {items.map((item) => (
        <li key={item.text} className="flex items-start gap-3 px-1 py-1.5">
          <span className="inline-flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full bg-white/12">
            <Icon name={item.icon as any} size={15} className="text-cyan-100" />
          </span>
          <span className="leading-relaxed text-white/92">{item.text}</span>
        </li>
      ))}
    </ul>
  );

  const renderCardIntro = (eyebrow: string, title: string, accent: string) => (
    <div className="space-y-3">
      <div className="inline-flex items-center gap-2 rounded-full bg-white/10 px-3 py-1.5 text-[11px] font-semibold uppercase tracking-[0.18em] text-white/85">
        <span className={`h-2 w-2 rounded-full ${accent}`} />
        {eyebrow}
      </div>
      <h2 className="text-[1.7rem] sm:text-[1.95rem] font-black leading-tight tracking-tight text-white drop-shadow-[0_2px_10px_rgba(0,0,0,0.18)]">
        {title}
      </h2>
    </div>
  );

  return (
    <div className="app-shell min-h-screen text-white" style={{ backgroundImage: 'var(--gradient-app)' }}>
      {showOnboarding && user && (
        <GeneralEntrepreneurOnboarding
          userId={user.id}
          onSkip={() => setShowOnboarding(false)}
        />
      )}
      <Header />
      <main className="px-6 pt-24 pb-12 sm:pt-28 sm:pb-16 min-h-screen flex items-center">
        <div className="max-w-6xl mx-auto w-full -translate-y-3 sm:-translate-y-6">
          <div className="text-center mb-10 sm:mb-12 flex flex-col items-center gap-4">
            <h1 className="text-3xl sm:text-5xl font-black tracking-tight text-white">
              {ideaMode ? '¿Cómo quieres trabajar tu idea?' : '¿Qué quieres hacer hoy?'}
            </h1>
            {ideaMode ? (
              <button
                type="button"
                onClick={() => setIdeaMode(false)}
                className="btn-secondary-app px-4 py-2 text-sm"
              >
                <Icon name="ArrowLeftIcon" size={16} className="text-primary" />
                Volver
              </button>
            ) : null}
          </div>

          {!ideaMode ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="app-glass-dark relative overflow-hidden rounded-2xl shadow-xl transition-all duration-300 hover:-translate-y-1 hover:shadow-2xl">
                <Image
                  src="/assets/images/AI_coach-1770284266834.png"
                  alt="AI Coach"
                  fill
                  className="object-cover object-[50%_30%] opacity-35"
                  sizes="(max-width: 768px) 100vw, 50vw"
                  priority
                />
                <div className="pointer-events-none absolute inset-0 bg-gradient-to-br from-[#081a3a]/78 via-[#0061ff]/20 to-[#081a3a]/78" />
                <div className="relative p-6 sm:p-8 text-center">
                  {renderCardIntro('AI Coach', 'Desarrolla tu negocio con foco, estructura y decisiones claras.', 'bg-emerald-300')}
                  {renderBenefitList([
                    { icon: 'ClipboardDocumentCheckIcon', text: 'Diagnóstico inteligente de tu negocio' },
                    { icon: 'ChartBarIcon', text: 'Planes de mejora accionables' },
                    { icon: 'HandRaisedIcon', text: 'Acompañamiento paso a paso' },
                  ])}
                  <Link
                    href={buildCoachHref('/ai-business-coach')}
                    className="coach-cta coach-cta-ai mt-6 inline-flex w-full px-5 py-3"
                  >
                    <Icon name="ArrowRightIcon" size={18} />
                    Ir al Coach con IA
                  </Link>
                </div>
              </div>

              <div className="app-glass-dark relative overflow-hidden rounded-2xl shadow-xl transition-all duration-300 hover:-translate-y-1 hover:shadow-2xl">
                <Image
                  src="/assets/images/aide_ideamaker-1770282880344.png"
                  alt="AIde"
                  fill
                  className="object-cover object-[60%_25%] opacity-35"
                  sizes="(max-width: 768px) 100vw, 50vw"
                />
                <div className="pointer-events-none absolute inset-0 bg-gradient-to-br from-[#081a3a]/78 via-[#0061ff]/20 to-[#081a3a]/78" />
                <div className="relative p-6 sm:p-8 text-center">
                  {renderCardIntro('Ruta de ideas', 'Activa, expande y aterriza una idea antes de que se enfríe.', 'bg-yellow-300')}
                  {renderBenefitList([
                    { icon: 'LightBulbIcon', text: 'Hacer una lluvia de ideas' },
                    { icon: 'RocketLaunchIcon', text: 'Convertir una idea en un proyecto concreto' },
                    { icon: 'ArrowsRightLeftIcon', text: 'Testear tu idea con pensamiento paralelo' },
                  ])}
                  <button
                    type="button"
                    onClick={() => setIdeaMode(true)}
                    className="coach-cta coach-cta-aide mt-6 inline-flex w-full px-5 py-3"
                  >
                    <Icon name="ArrowRightIcon" size={18} />
                    ¡Trabajar mi idea!
                  </button>
                </div>
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="app-glass-dark relative overflow-hidden rounded-2xl shadow-xl transition-all duration-300 hover:-translate-y-1 hover:shadow-2xl">
                <Image
                  src="/assets/images/genia_lluvia_ideas-1770283270530.png"
                  alt="Gen-IA"
                  fill
                  className="object-cover object-[55%_30%] opacity-35"
                  sizes="(max-width: 768px) 100vw, 33vw"
                />
                <div className="pointer-events-none absolute inset-0 bg-gradient-to-br from-[#081a3a]/78 via-[#0061ff]/20 to-[#081a3a]/78" />
                <div className="relative p-6 sm:p-7 text-center">
                  {renderCardIntro('Gen-IA', 'Si no tienes una idea, parte con una lluvia guiada.', 'bg-cyan-300')}
                  {renderBenefitList([
                    { icon: 'SparklesIcon', text: 'Lluvia de ideas sin presión' },
                    { icon: 'MapIcon', text: 'Inspiración según tu contexto' },
                    { icon: 'ArrowTrendingUpIcon', text: 'Primeros pasos claros' },
                  ])}
                  <Link
                    href={buildCoachHref('/brainstorm-coach')}
                    className="coach-cta coach-cta-genia mt-6 inline-flex w-full px-5 py-3"
                  >
                    <Icon name="ArrowRightIcon" size={18} />
                    Haz una lluvia de ideas con Gen-IA
                  </Link>
                </div>
              </div>

              <div className="app-glass-dark relative overflow-hidden rounded-2xl shadow-xl transition-all duration-300 hover:-translate-y-1 hover:shadow-2xl">
                <Image
                  src="/assets/images/socriates_pensamiento_paralelo-1770284247284.png"
                  alt="SocrIAtes"
                  fill
                  className="object-cover object-[55%_25%] opacity-35"
                  sizes="(max-width: 768px) 100vw, 33vw"
                />
                <div className="pointer-events-none absolute inset-0 bg-gradient-to-br from-[#081a3a]/78 via-[#0061ff]/20 to-[#081a3a]/78" />
                <div className="relative p-6 sm:p-7 text-center">
                  {renderCardIntro('SocrIAtes', 'Desafía y fortalece tu idea con preguntas difíciles.', 'bg-fuchsia-300')}
                  {renderBenefitList([
                    { icon: 'ExclamationTriangleIcon', text: 'Detecta supuestos débiles' },
                    { icon: 'AdjustmentsHorizontalIcon', text: 'Mejora tu enfoque' },
                    { icon: 'ShieldCheckIcon', text: 'Pensamiento crítico aplicado' },
                  ])}
                  <Link
                    href={buildCoachHref('/obx')}
                    className="coach-cta coach-cta-socriates mt-6 inline-flex w-full px-5 py-3"
                  >
                    <Icon name="ArrowRightIcon" size={18} />
                    Testea tu idea con SocrIAtes
                  </Link>
                </div>
              </div>

              <div className="app-glass-dark relative overflow-hidden rounded-2xl shadow-xl transition-all duration-300 hover:-translate-y-1 hover:shadow-2xl">
                <Image
                  src="/assets/images/aide_ideamaker-1770282880344.png"
                  alt="AIde"
                  fill
                  className="object-cover object-[60%_25%] opacity-35"
                  sizes="(max-width: 768px) 100vw, 33vw"
                />
                <div className="pointer-events-none absolute inset-0 bg-gradient-to-br from-[#081a3a]/78 via-[#0061ff]/20 to-[#081a3a]/78" />
                <div className="relative p-6 sm:p-7 text-center">
                  {renderCardIntro('AIde', 'Convierte una idea en un proyecto concreto.', 'bg-amber-300')}
                  {renderBenefitList([
                    { icon: 'TagIcon', text: 'Define propuesta de valor' },
                    { icon: 'Squares2X2Icon', text: 'Ordena el modelo de negocio' },
                    { icon: 'FlagIcon', text: 'Aterriza tu idea a la realidad' },
                  ])}
                  <Link
                    href={buildCoachHref('/ai-idea-coach')}
                    className="coach-cta coach-cta-aide mt-6 inline-flex w-full px-5 py-3"
                  >
                    <Icon name="ArrowRightIcon" size={18} />
                    Tu idea a proyecto con AIde!
                  </Link>
                </div>
              </div>
            </div>
          )}

          <div className="mt-6 text-sm text-indigo-200">
            {/* Rutas elegidas por claridad: /ai-business-coach, /ai-idea-coach, /brainstorm-coach, /obx */}
          </div>
        </div>
      </main>
    </div>
  );
}
